import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";

import { searchSettings, type SettingsSearchItem } from "./settingsSearch";

/**
 * The one settings-search interaction, shared by every surface that hosts it
 * (the routed settings sidebar and the room's Canvas settings surface):
 * query state, result cycling with wrap-around, Enter to open, Escape to
 * clear, and the combobox/listbox ARIA contract. Router-free by design —
 * what "open" means (a navigation, or surface-internal state) is the host's
 * `onSelect`, so the Canvas surface never reaches for app navigation (WS5).
 */
export function useSettingsSearch({
  onSelect,
  resultIdPrefix = "settings-search-result",
  listboxId = "settings-search-results",
}: {
  readonly onSelect: (item: SettingsSearchItem) => void;
  /** DOM id prefix for result options — distinct per host so ids never collide. */
  readonly resultIdPrefix?: string;
  readonly listboxId?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [activeResultIndex, setActiveResultIndex] = useState(0);
  const results = useMemo(() => searchSettings(query), [query]);
  const isSearching = query.trim().length > 0;
  const hasResults = results.length > 0;

  const resultDomId = useCallback(
    (item: SettingsSearchItem) => `${resultIdPrefix}-${item.id}`,
    [resultIdPrefix],
  );

  useEffect(() => {
    const result = results[activeResultIndex];
    if (!result) return;
    document.getElementById(`${resultIdPrefix}-${result.id}`)?.scrollIntoView({ block: "nearest" });
  }, [activeResultIndex, resultIdPrefix, results]);

  const clear = useCallback(() => {
    setQuery("");
    setActiveResultIndex(0);
  }, []);

  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
    setActiveResultIndex(0);
  }, []);

  const select = useCallback(
    (item: SettingsSearchItem) => {
      clear();
      onSelect(item);
    },
    [clear, onSelect],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape" && isSearching) {
        event.preventDefault();
        event.stopPropagation();
        clear();
        return;
      }
      if (results.length === 0) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveResultIndex((index) => (index + 1) % results.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveResultIndex((index) => (index - 1 + results.length) % results.length);
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        const result = results[activeResultIndex];
        if (result) select(result);
      }
    },
    [activeResultIndex, clear, isSearching, results, select],
  );

  const comboboxProps = {
    role: "combobox" as const,
    "aria-autocomplete": "list" as const,
    "aria-expanded": isSearching && hasResults,
    "aria-controls": isSearching && hasResults ? listboxId : undefined,
    "aria-activedescendant":
      isSearching && results[activeResultIndex]
        ? resultDomId(results[activeResultIndex])
        : undefined,
  };

  return {
    activeResultIndex,
    clear,
    comboboxProps,
    handleKeyDown,
    handleQueryChange,
    hasResults,
    inputRef,
    isSearching,
    listboxId,
    query,
    resultDomId,
    results,
    select,
    setActiveResultIndex,
  };
}

/**
 * The `/` hotkey that focuses a settings search input, guarded so it never
 * steals the key from editors, dialogs, or popups. `onBeforeFocus` lets a
 * host reveal its chrome first (the sidebar opens itself on mobile); focus
 * lands on the next frame so that reveal can mount the input.
 */
export function useSettingsSearchSlashHotkey(
  inputRef: RefObject<HTMLInputElement | null>,
  onBeforeFocus?: () => void,
) {
  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable ||
          // Keep focus inside open dialogs and popups instead of escaping
          // their focus trap into the settings search.
          target.closest('[role="dialog"], [aria-modal="true"], [data-slot$="popup"]') !== null)
      ) {
        return;
      }

      event.preventDefault();
      onBeforeFocus?.();
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inputRef, onBeforeFocus]);
}
